import { useState, useEffect } from 'react'
import { db } from '../firebase/config'
import { collection, getDocs, addDoc, doc, updateDoc, increment, Timestamp, query, where } from 'firebase/firestore'
import { Search, Calendar, Eye, Heart, Share2, MessageCircle, User, Send, Tag } from 'lucide-react'

// Convertit une date Firestore (Timestamp réel OU objet {_seconds,_nanoseconds}
// issu d'une donnée importée via JSON) en vrai objet Date JS.
function toDateSafe(value) {
  if (!value) return new Date(0)
  if (typeof value.toDate === 'function') return value.toDate()
  if (typeof value._seconds === 'number') return new Date(value._seconds * 1000)
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000)
  const d = new Date(value)
  return isNaN(d.getTime()) ? new Date(0) : d
}

// ─── Injection intelligente de sauts de ligne ────────────
function smartBreaks(text) {
  if (!text) return ''

  // 1. Si c'est du HTML avec des balises de bloc → on le laisse tel quel
  if (/<(p|br|div|h[1-6]|ul|ol|li)[\s>/]/i.test(text)) return text

  let t = text

  // 2. Remplacer les \n existants
  t = t.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

  // 3. Insérer un saut AVANT chaque émoji "marqueur de section"
  //    (émoji précédé d'un espace ou de texte, pas en début de chaîne)
  const SECTION_EMOJIS = [
    '📅','📍','🎸','🎵','🎶','🎤','🎙','🎪','🎭','✨','💫',
    '🔔','📢','📣','⚡','🌟','🎉','🎊','🎈','🏠','🕕','🕗',
    '🕘','🕙','🕚','🕛','🕐','🕑','🕒','🕓','⏰','📧','📞',
    '📱','💻','🌐','🔗','👥','👤','🗓','📆','🚀','❤','💌',
    '🎯','🎼','🎹','🥁','🎻','🎺','🎷','🔊','📻','🏆','🥇',
    '🏅','📸','📷','🎬','🎥','📺','🎞','⭐','🌙','☀','🌍',
    '🌺','🌸','🌹','🌻','🌼','🍀','🌿','🍃','📝','✍','🖊',
    '💡','🔮','🎸','🎤','🚩','🚗','✈','🚂','🚢'
  ]

  // Regex qui matche un émoji marqueur précédé d'un caractère non-newline
  const emojiPattern = new RegExp(
    `([^\\n])(${SECTION_EMOJIS.map(e => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'g'
  )
  t = t.replace(emojiPattern, '$1\n$2')

  // 4. Insérer un saut avant les heures type "18h30", "20h30", "22h30"
  t = t.replace(/([^\n])(\d{1,2}h\d{2})/g, '$1\n$2')

  // 5. Insérer un saut avant les tirets de liste "– " ou "- " en milieu de texte
  t = t.replace(/([^\n]) (–|—|-) /g, '$1\n$2 ')

  // 6. Insérer un saut avant "Infos", "Contact", "Programme", etc. (mots clés de section)
  const KEYWORDS = ['Infos ', 'Contact ', 'Programme ', 'Invitez ', 'Venez ', 'Retrouvez ']
  KEYWORDS.forEach(kw => {
    t = t.replace(new RegExp(`([^\\n])(${kw})`, 'g'), '$1\n$2')
  })

  // 7. Nettoyer les doubles sauts
  t = t.replace(/\n{3,}/g, '\n\n')

  // 8. Convertir \n → <br>
  return t.replace(/\n/g, '<br />')
}

// ─── Rendu du contenu avec vrais sauts de ligne ───────────
function ArticleContent({ html }) {
  if (!html) return null
  const processed = smartBreaks(html)
  return (
    <div
      style={{
        fontSize: 'clamp(0.95rem, 1.9vw, 1.1rem)',
        lineHeight: 1.85,
        color: 'rgba(255,255,255,0.9)'
      }}
      dangerouslySetInnerHTML={{ __html: processed }}
    />
  )
}

// ─── Extrait avec sauts de ligne lisibles ─────────────────
function Excerpt({ text, maxLines }) {
  if (!text) return null
  return (
    <p style={{
      color: 'rgba(255,255,255,0.72)',
      lineHeight: 1.65,
      margin: 0,
      whiteSpace: 'pre-line', // ← respecte les \n natifs
      display: '-webkit-box',
      WebkitLineClamp: maxLines || 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    }}>
      {text}
    </p>
  )
}

// ─── Badge catégorie ──────────────────────────────────────
function CatBadge({ category, categories, size = 'sm' }) {
  const cat = categories.find(c => c.id === category)
  if (!category || !cat) return null
  const p = size === 'lg' ? '6px 14px' : '4px 10px'
  const fs = size === 'lg' ? '0.88rem' : '0.72rem'
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: p, borderRadius: 10, fontSize: fs, fontWeight: 700,
      textTransform: 'capitalize',
      background: 'rgba(255,215,0,0.14)',
      border: '1px solid rgba(255,215,0,0.3)',
      color: '#ffd700'
    }}>
      {cat.icon} {category}
    </span>
  )
}

// ─── Stat badge ───────────────────────────────────────────
function StatBadge({ icon, value, label, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7,
      padding: 'clamp(8px,1.4vh,12px) clamp(12px,2vw,18px)',
      background: `${color}14`,
      borderRadius: 12, flex: '1 1 0', justifyContent: 'center',
      minWidth: 0
    }}>
      <span style={{ color, flexShrink: 0 }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: 'clamp(0.95rem,1.9vw,1.1rem)' }}>{value}</span>
      <span style={{ fontSize: 'clamp(0.72rem,1.4vw,0.82rem)', color: 'rgba(255,255,255,0.5)', display: 'none' }}
        className="stat-label">{label}</span>
    </div>
  )
}

// ─── Page principale ──────────────────────────────────────
function BlogPage({ currentUser }) {
  const [articles, setArticles]               = useState([])
  const [comments, setComments]               = useState({})
  const [loading, setLoading]                 = useState(true)
  const [searchTerm, setSearchTerm]           = useState('')
  const [currentPage, setCurrentPage]         = useState(1)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedArticle, setSelectedArticle] = useState(null)
  const [showShareMenu, setShowShareMenu]     = useState(false)
  const [showCommentForm, setShowCommentForm] = useState(false)
  const [commentText, setCommentText]         = useState('')
  const [submitting, setSubmitting]           = useState(false)

  const ITEMS_PER_PAGE = 10

  const categories = [
    { id: 'all',        label: 'Tous',        icon: '📰' },
    { id: 'news',       label: 'Actualités',  icon: '📢' },
    { id: 'concerts',   label: 'Concerts',    icon: '🎸' },
    { id: 'hommages',   label: 'Hommages',    icon: '🙏' },
    { id: 'interviews', label: 'Interviews',  icon: '🎤' },
    { id: 'histoire',   label: 'Histoire',    icon: '📚' },
  ]

  useEffect(() => { loadArticles() }, [])

  useEffect(() => {
    if (selectedArticle) {
      updateMetaTags(selectedArticle)
      incrementViews(selectedArticle.id)
      loadComments(selectedArticle.id)
    } else {
      resetMetaTags()
    }
  }, [selectedArticle?.id])

  // ── Meta tags ────────────────────────────────────────────
  const setMeta = (prop, content) => {
    let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`)
    if (!el) {
      el = document.createElement('meta')
      el.setAttribute(prop.startsWith('og:') || prop.startsWith('twitter:') ? 'property' : 'name', prop)
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  }
  const updateMetaTags = (a) => {
    document.title = `${a.title || 'Article'} - Radio 100% Johnny`
    setMeta('og:title', a.title || ''); setMeta('og:description', a.excerpt || '')
    setMeta('og:image', a.coverImage || a.imageUrl || ''); setMeta('og:type', 'article')
    setMeta('description', a.excerpt || '')
  }
  const resetMetaTags = () => {
    document.title = 'Radio 100% Johnny'
    setMeta('og:title', 'Radio 100% Johnny'); setMeta('description', 'Écoutez Johnny Hallyday 24h/24')
  }

  // ── Firebase ─────────────────────────────────────────────
  const loadArticles = async () => {
    setLoading(true)
    try {
      const snap = await getDocs(query(collection(db, '100johnny', 'main', 'blogs'), where('status', '==', 'published')))
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      data.sort((a, b) => {
        return toDateSafe(b.publishDate) - toDateSafe(a.publishDate)
      })
      setArticles(data)
      // Commentaires en parallèle
      const all = {}
      await Promise.all(data.map(async article => {
        try {
          const cs = await getDocs(query(
            collection(db, '100johnny', 'main', 'blog_comments'),
            where('articleId', '==', article.id),
            where('approved', '==', true)
          ))
          all[article.id] = cs.docs.map(d => ({ id: d.id, ...d.data() }))
        } catch { all[article.id] = [] }
      }))
      setComments(all)
    } catch (err) {
      console.error('Erreur chargement articles:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadComments = async (articleId) => {
    try {
      const snap = await getDocs(query(
        collection(db, '100johnny', 'main', 'blog_comments'),
        where('articleId', '==', articleId),
        where('approved', '==', true)
      ))
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          return toDateSafe(b.createdAt) - toDateSafe(a.createdAt)
        })
      setComments(prev => ({ ...prev, [articleId]: data }))
      setSelectedArticle(prev => prev ? { ...prev, stats: { ...prev.stats, comments: data.length } } : prev)
    } catch (err) { console.error(err) }
  }

  const incrementViews = async (id) => {
    try { await updateDoc(doc(db, '100johnny', 'main', 'blogs', id), { 'stats.views': increment(1) }) } catch {}
  }

  const handleLike = async (id) => {
    try {
      await updateDoc(doc(db, '100johnny', 'main', 'blogs', id), { 'stats.likes': increment(1) })
      setSelectedArticle(prev => ({ ...prev, stats: { ...prev.stats, likes: (prev.stats?.likes || 0) + 1 } }))
    } catch (err) { console.error(err) }
  }

  const handleShare = async (platform, article) => {
    const url = window.location.href
    const title = article.title || 'Article Radio Johnny'
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      copy: url
    }
    if (platform === 'copy') {
      try { await navigator.clipboard.writeText(url) } catch { /* fallback */ }
      alert('✅ Lien copié !')
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400')
    }
    try {
      await updateDoc(doc(db, '100johnny', 'main', 'blogs', article.id), {
        'stats.shares': increment(1),
        [`stats.sharesByPlatform.${platform}`]: increment(1)
      })
      setSelectedArticle(prev => ({ ...prev, stats: { ...prev.stats, shares: (prev.stats?.shares || 0) + 1 } }))
    } catch {}
    setShowShareMenu(false)
  }

  const handleSubmitComment = async (e) => {
    e.preventDefault()
    if (!currentUser) { alert('⚠️ Vous devez être connecté pour commenter'); return }
    if (!commentText.trim()) { alert('⚠️ Veuillez écrire un commentaire'); return }
    setSubmitting(true)
    try {
      await addDoc(collection(db, '100johnny', 'main', 'blog_comments'), {
        articleId: selectedArticle.id,
        authorName: currentUser.displayName || 'Anonyme',
        text: commentText, userId: currentUser.uid,
        createdAt: Timestamp.now(), approved: false
      })
      await updateDoc(doc(db, '100johnny', 'main', 'blogs', selectedArticle.id), { 'stats.comments': increment(1) })
      alert('✅ Commentaire envoyé ! Il sera visible après validation.')
      setCommentText(''); setShowCommentForm(false)
    } catch (err) {
      console.error(err); alert('❌ Erreur lors de l\'envoi')
    } finally { setSubmitting(false) }
  }

  // ── Helpers ───────────────────────────────────────────────
  const formatDate = (date) => {
    if (!date) return 'Date inconnue'
    try {
      const d = toDateSafe(date)
      return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    } catch { return '' }
  }

  const getAuthor = (a) => {
    if (!a) return 'Auteur inconnu'
    return typeof a === 'string' ? a : a.name || 'Auteur inconnu'
  }

  // ── Filtres ───────────────────────────────────────────────
  const filtered = articles.filter(a => {
    const matchCat = selectedCategory === 'all' || a.category === selectedCategory
    const s = searchTerm.toLowerCase()
    const matchSearch = !s ||
      a.title?.toLowerCase().includes(s) ||
      a.excerpt?.toLowerCase().includes(s) ||
      getAuthor(a.author).toLowerCase().includes(s)
    return matchCat && matchSearch
  })

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE)
  const paginated  = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  // ════════════════════════════════════════════════════════
  // VUE ARTICLE DÉTAIL
  // ════════════════════════════════════════════════════════
  if (selectedArticle) {
    const stats = selectedArticle.stats || {}
    const cover = selectedArticle.coverImage || selectedArticle.imageUrl
    const articleComments = comments[selectedArticle.id] || []

    return (
      <div className="page active">
        <div style={{
          maxWidth: 860,
          margin: '0 auto',
          padding: 'clamp(12px, 2vw, 24px)',
          paddingBottom: 80
        }}>

          {/* Retour */}
          <button
            className="btn btn-secondary"
            onClick={() => { setSelectedArticle(null); setShowCommentForm(false); setCommentText('') }}
            style={{ marginBottom: 'clamp(14px, 2.5vh, 22px)', display: 'inline-flex', alignItems: 'center', gap: 7 }}
          >
            ← Retour au blog
          </button>

          <div className="glass-card" style={{ padding: 'clamp(16px, 3vw, 32px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2.8vh, 26px)' }}>

            {/* Image de couverture */}
            {cover && (
              <img
                src={cover} alt={selectedArticle.title}
                style={{ width: '100%', maxHeight: 'clamp(200px, 40vh, 480px)', objectFit: 'cover', borderRadius: 14 }}
              />
            )}

            {/* Badges */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <CatBadge category={selectedArticle.category} categories={categories} size="lg" />
              {selectedArticle.featured && (
                <span style={{ padding: '6px 14px', borderRadius: 10, fontSize: '0.88rem', fontWeight: 700, background: 'rgba(0,217,255,0.14)', border: '1px solid rgba(0,217,255,0.3)', color: '#00d9ff' }}>
                  ⭐ À la une
                </span>
              )}
            </div>

            {/* Titre */}
            <h1 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.4rem)', fontWeight: 800, lineHeight: 1.25, margin: 0 }}>
              {selectedArticle.title || 'Article sans titre'}
            </h1>

            {/* Résumé / extrait */}
            {selectedArticle.excerpt && (
              <p style={{
                fontSize: 'clamp(0.95rem, 1.9vw, 1.15rem)',
                color: 'rgba(255,255,255,0.78)',
                lineHeight: 1.7, fontStyle: 'italic',
                whiteSpace: 'pre-line', /* ← sauts de ligne respectés */
                margin: 0,
                padding: 'clamp(12px,2vh,18px)',
                background: 'rgba(255,255,255,0.04)',
                borderLeft: '3px solid rgba(0,217,255,0.5)',
                borderRadius: '0 12px 12px 0'
              }}>
                {selectedArticle.excerpt}
              </p>
            )}

            {/* Méta auteur / date */}
            <div style={{ display: 'flex', gap: 'clamp(10px,2vw,20px)', flexWrap: 'wrap', fontSize: 'clamp(0.8rem,1.6vw,0.92rem)', color: 'rgba(255,255,255,0.55)', paddingBottom: 'clamp(12px,2vh,18px)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              {selectedArticle.author && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <User size={14} /> {getAuthor(selectedArticle.author)}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Calendar size={14} /> {formatDate(selectedArticle.publishDate || selectedArticle.createdAt)}
              </div>
              <div>⏱️ {selectedArticle.readTime || 5} min</div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatBadge icon={<Eye size={16}/>} value={stats.views || 0} label="vues" color="#00d9ff" />
              <StatBadge icon={<Heart size={16}/>} value={stats.likes || 0} label="likes" color="#ff1493" />
              <StatBadge icon={<Share2 size={16}/>} value={stats.shares || 0} label="partages" color="#ffd700" />
              <StatBadge icon={<MessageCircle size={16}/>} value={articleComments.length} label="commentaires" color="#9333ea" />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={() => handleLike(selectedArticle.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: 'clamp(10px,1.8vh,13px) clamp(16px,2.5vw,22px)',
                  background: 'rgba(255,20,147,0.14)', border: '1px solid rgba(255,20,147,0.35)',
                  borderRadius: 14, color: '#ff1493', fontWeight: 700,
                  cursor: 'pointer', fontSize: 'clamp(0.85rem,1.7vw,0.95rem)'
                }}
              >
                <Heart size={16} /> J'aime
              </button>

              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowShareMenu(v => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    padding: 'clamp(10px,1.8vh,13px) clamp(16px,2.5vw,22px)',
                    background: 'rgba(255,215,0,0.14)', border: '1px solid rgba(255,215,0,0.35)',
                    borderRadius: 14, color: '#ffd700', fontWeight: 700,
                    cursor: 'pointer', fontSize: 'clamp(0.85rem,1.7vw,0.95rem)'
                  }}
                >
                  <Share2 size={16} /> Partager
                </button>

                {showShareMenu && (
                  <div style={{
                    position: 'absolute', top: '110%', left: 0, zIndex: 100,
                    background: 'rgba(15,15,15,0.97)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 14, padding: 10, minWidth: 190,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.6)'
                  }}>
                    {[
                      { key: 'facebook', icon: '📘', label: 'Facebook'     },
                      { key: 'twitter',  icon: '🐦', label: 'Twitter/X'    },
                      { key: 'whatsapp', icon: '💬', label: 'WhatsApp'     },
                      { key: 'telegram', icon: '✈️', label: 'Telegram'     },
                      { key: 'copy',     icon: '📋', label: 'Copier le lien' },
                    ].map(p => (
                      <button
                        key={p.key}
                        onClick={() => handleShare(p.key, selectedArticle)}
                        style={{
                          width: '100%', padding: '9px 12px', background: 'transparent',
                          border: 'none', color: '#fff', textAlign: 'left', cursor: 'pointer',
                          borderRadius: 8, fontSize: '0.9rem', transition: 'background 0.15s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {p.icon} {p.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Contenu de l'article — sauts de ligne propres */}
            <div style={{
              paddingTop: 'clamp(12px,2vh,18px)',
              borderTop: '1px solid rgba(255,255,255,0.08)'
            }}>
              <ArticleContent html={selectedArticle.content} />
            </div>

            {/* Tags */}
            {selectedArticle.tags?.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', paddingTop: 'clamp(12px,2vh,18px)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <Tag size={15} color="rgba(255,255,255,0.45)" />
                {selectedArticle.tags.map((t, i) => (
                  <span key={i} style={{ padding: '5px 11px', background: 'rgba(0,217,255,0.1)', border: '1px solid rgba(0,217,255,0.25)', borderRadius: 20, fontSize: '0.82rem', color: '#00d9ff' }}>
                    #{t}
                  </span>
                ))}
              </div>
            )}

            {/* ── COMMENTAIRES ────────────────────────────── */}
            <div style={{ paddingTop: 'clamp(20px,3.5vh,32px)', borderTop: '2px solid rgba(255,255,255,0.1)' }}>
              <h2 style={{ fontSize: 'clamp(1.2rem,2.5vw,1.6rem)', fontWeight: 700, marginBottom: 'clamp(14px,2.5vh,22px)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <MessageCircle size={24} color="#9333ea" />
                Commentaires ({articleComments.length})
              </h2>

              {currentUser ? (
                showCommentForm ? (
                  <form onSubmit={handleSubmitComment} style={{ marginBottom: 'clamp(16px,2.8vh,24px)', padding: 'clamp(14px,2.5vh,20px)', background: 'rgba(147,51,234,0.06)', borderRadius: 14, border: '1px solid rgba(147,51,234,0.2)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <textarea
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      placeholder="Partagez votre avis..."
                      rows={4} required
                      style={{ width: '100%', boxSizing: 'border-box', padding: 'clamp(10px,1.8vh,14px)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, color: '#fff', fontSize: 'clamp(0.88rem,1.7vw,1rem)', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                    />
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button type="submit" disabled={submitting} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 7, opacity: submitting ? 0.6 : 1 }}>
                        <Send size={16} /> {submitting ? 'Envoi...' : 'Envoyer'}
                      </button>
                      <button type="button" onClick={() => { setShowCommentForm(false); setCommentText('') }} className="btn btn-secondary">
                        Annuler
                      </button>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', margin: 0 }}>ⓘ Visible après validation par la modération</p>
                  </form>
                ) : (
                  <button onClick={() => setShowCommentForm(true)} className="btn btn-primary" style={{ marginBottom: 'clamp(16px,2.8vh,24px)', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                    <MessageCircle size={16} /> Laisser un commentaire
                  </button>
                )
              ) : (
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', marginBottom: 20 }}>🔐 Connectez-vous pour commenter</p>
              )}

              {articleComments.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.45)', padding: 'clamp(20px,4vh,40px)', fontSize: 'clamp(0.9rem,1.8vw,1.05rem)' }}>
                  Aucun commentaire pour le moment. Soyez le premier !
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px,1.8vh,16px)' }}>
                  {articleComments.map(c => (
                    <div key={c.id} style={{ padding: 'clamp(12px,2.2vh,18px)', background: 'rgba(147,51,234,0.05)', borderRadius: 14, border: '1px solid rgba(147,51,234,0.18)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#9333ea,#ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                          {c.authorName?.charAt(0).toUpperCase() || 'A'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 'clamp(0.85rem,1.7vw,0.95rem)' }}>{c.authorName || 'Anonyme'}</div>
                          <div style={{ fontSize: 'clamp(0.72rem,1.4vw,0.8rem)', color: 'rgba(255,255,255,0.45)' }}>{formatDate(c.createdAt)}</div>
                        </div>
                      </div>
                      {/* Texte commentaire avec sauts de ligne */}
                      <p style={{ margin: 0, fontSize: 'clamp(0.88rem,1.7vw,1rem)', lineHeight: 1.65, color: 'rgba(255,255,255,0.88)', whiteSpace: 'pre-line' }}>
                        {c.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ════════════════════════════════════════════════════════
  // VUE LISTE
  // ════════════════════════════════════════════════════════
  return (
    <div className="page active">
      <div style={{
        maxWidth: '100%',
        width: '100%',
        padding: 'clamp(12px, 2vw, 24px)',
        paddingBottom: 80,
        boxSizing: 'border-box'
      }}>

        {/* HEADER */}
        <div className="page-header" style={{ marginBottom: 'clamp(14px,2.5vh,22px)' }}>
          <div className="page-badge">📰 BLOG</div>
          <h1 className="page-title">Blog</h1>
          <p className="page-subtitle">Actualités et articles sur Johnny</p>
        </div>

        {/* Filtres catégorie */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 'clamp(12px,2vh,18px)', flexWrap: 'wrap' }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => { setSelectedCategory(cat.id); setCurrentPage(1) }}
              style={{
                padding: 'clamp(8px,1.4vh,11px) clamp(12px,2vw,18px)',
                background: selectedCategory === cat.id
                  ? 'linear-gradient(135deg,#00d9ff,#ffd700)'
                  : 'rgba(255,255,255,0.06)',
                border: selectedCategory === cat.id ? 'none' : '1px solid rgba(255,255,255,0.15)',
                borderRadius: 12,
                color: selectedCategory === cat.id ? '#000' : '#fff',
                fontWeight: 700, cursor: 'pointer',
                fontSize: 'clamp(0.78rem,1.5vw,0.9rem)',
                transition: 'all 0.2s'
              }}
            >
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>

        {/* Recherche */}
        <div className="glass-card" style={{ padding: 'clamp(10px,1.8vh,14px)', marginBottom: 'clamp(14px,2.5vh,20px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Search size={18} color="rgba(255,255,255,0.45)" />
            <input
              type="text"
              placeholder="Rechercher un article..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1) }}
              style={{ flex: 1, background: 'transparent', border: 'none', color: '#fff', outline: 'none', fontSize: 'clamp(0.88rem,1.7vw,1rem)' }}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /><p>Chargement...</p></div>
        ) : paginated.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📰</div>
            <p>{searchTerm ? 'Aucun article trouvé' : 'Aucun article pour le moment'}</p>
          </div>
        ) : (
          <>
            {/* Grille articles — 2 colonnes sur desktop, 1 sur mobile */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(280px, 40%, 420px), 1fr))',
              gap: 'clamp(12px, 2vw, 20px)',
              marginBottom: 'clamp(16px, 2.8vh, 24px)'
            }}>
              {paginated.map(article => {
                const stats   = article.stats || {}
                const cover   = article.coverImage || article.imageUrl
                const nbCom   = comments[article.id]?.length ?? stats.comments ?? 0

                return (
                  <div
                    key={article.id}
                    onClick={() => setSelectedArticle(article)}
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 18, cursor: 'pointer',
                      overflow: 'hidden', transition: 'all 0.25s',
                      display: 'flex', flexDirection: 'column'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.border = '1px solid rgba(0,217,255,0.35)'; e.currentTarget.style.transform = 'translateY(-2px)' }}
                    onMouseLeave={e => { e.currentTarget.style.border = '1px solid rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'none' }}
                  >
                    {cover && (
                      <img
                        src={cover} alt={article.title}
                        style={{ width: '100%', height: 'clamp(140px, 22vh, 200px)', objectFit: 'cover', display: 'block' }}
                      />
                    )}

                    <div style={{ padding: 'clamp(12px,2vw,18px)', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {/* Badges */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <CatBadge category={article.category} categories={categories} />
                        {article.featured && (
                          <span style={{ padding: '4px 10px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700, background: 'rgba(0,217,255,0.12)', border: '1px solid rgba(0,217,255,0.28)', color: '#00d9ff' }}>
                            ⭐ À la une
                          </span>
                        )}
                      </div>

                      {/* Titre */}
                      <h3 style={{ fontSize: 'clamp(1rem,2.2vw,1.25rem)', fontWeight: 800, lineHeight: 1.3, margin: 0 }}>
                        {article.title || 'Article sans titre'}
                      </h3>

                      {/* Extrait — sauts de ligne respectés */}
                      {article.excerpt && (
                        <Excerpt text={article.excerpt} maxLines={3} />
                      )}

                      {/* Footer meta */}
                      <div style={{
                        marginTop: 'auto', paddingTop: 10,
                        borderTop: '1px solid rgba(255,255,255,0.07)',
                        display: 'flex', alignItems: 'center',
                        gap: 'clamp(8px,1.5vw,14px)', flexWrap: 'wrap',
                        fontSize: 'clamp(0.72rem,1.4vw,0.82rem)',
                        color: 'rgba(255,255,255,0.45)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Calendar size={12} /> {formatDate(article.publishDate || article.createdAt)}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Eye size={12} /> {stats.views || 0}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Heart size={12} /> {stats.likes || 0}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MessageCircle size={12} /> {nbCom}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{ opacity: currentPage === 1 ? 0.45 : 1 }}
                >
                  ← Précédent
                </button>
                <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem' }}>
                  Page {currentPage} / {totalPages}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{ opacity: currentPage === totalPages ? 0.45 : 1 }}
                >
                  Suivant →
                </button>
              </div>
            )}

            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.42)', fontSize: '0.85rem' }}>
              {filtered.length} article{filtered.length > 1 ? 's' : ''} trouvé{filtered.length > 1 ? 's' : ''}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default BlogPage